/**
 * AS Fashion Hero — asfashion.store-style banner carousel with left/right
 * arrow controls, slide indicator dots, and auto-advance.
 *
 * Data source (priority order):
 *  1. GET /banners?placement=home_hero  — admin-managed multi-slide feed.
 *     Each row exposes: image_url, mobile_image_url, title, subtitle,
 *     cta_label, link_url.
 *  2. Fallback: the single `branding.hero` object so the preset still
 *     renders something meaningful before the admin uploads slides.
 *
 * Arrows are hidden on mobile (touch users swipe / tap the dots) and
 * hover-revealed on desktop, matching asfashion.store's UX.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api, imgUrl } from "../../../lib/api";

const HERO_BG_FALLBACK =
  "https://images.unsplash.com/photo-1520975916090-3105956dac38?auto=format&fit=crop&w=1920&q=80";
const AUTO_ADVANCE_MS = 6000;

/** Fetch admin-managed home_hero banners (empty array on error). */
function useHeroBanners() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/banners", {
          params: { placement: "home_hero" },
        });
        if (!cancelled) setBanners(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setBanners([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return { banners, loading };
}

/**
 * Build the effective slide list. When no admin banners exist we
 * synthesise a single slide from the tenant's branding.hero so the
 * carousel still renders (single slide → arrows + dots auto-hide).
 */
function buildSlides(banners, branding) {
  if (banners.length > 0) {
    return banners.map((b) => ({
      id: b.id,
      image_url: imgUrl(b.image_url),
      mobile_image_url: b.mobile_image_url ? imgUrl(b.mobile_image_url) : null,
      title: b.title || "",
      subtitle: b.subtitle || "",
      cta_label: b.cta_label || "",
      link_url: b.link_url || "/shop",
    }));
  }
  const h = branding?.hero || {};
  return [
    {
      id: "fallback",
      image_url: h.image_url || HERO_BG_FALLBACK,
      mobile_image_url: null,
      title: h.title_line_1 || "",
      subtitle: h.subtitle || h.eyebrow || "",
      cta_label: h.cta_label || "",
      link_url: h.cta_link || "/shop",
    },
  ];
}

export function AsFashionHero({ branding }) {
  const { banners } = useHeroBanners();
  const slides = buildSlides(banners, branding);
  const total = slides.length;
  const [active, setActive] = useState(0);
  const timerRef = useRef(null);

  // Auto-advance — pause while a real mouse is hovering, or while a
  // touch swipe is in progress. Resumes afterwards either way.
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (total <= 1 || paused) return undefined;
    timerRef.current = setInterval(
      () => setActive((i) => (i + 1) % total),
      AUTO_ADVANCE_MS,
    );
    return () => clearInterval(timerRef.current);
  }, [total, paused]);

  const goPrev = () => setActive((i) => (i - 1 + total) % total);
  const goNext = () => setActive((i) => (i + 1) % total);

  // Mobile WebViews (notably iOS WKWebView) can synthesize a
  // `mouseenter`-equivalent hover state from a tap without ever firing
  // a matching `mouseleave` — that permanently stuck `paused=true` on
  // the very first touch, which is why auto-advance appeared broken
  // on mobile. Pointer Events report the real input device via
  // `pointerType`, so gating on `=== "mouse"` only pauses for an
  // actual mouse hover and never for touch.
  const onPointerEnter = (e) => { if (e.pointerType === "mouse") setPaused(true); };
  const onPointerLeave = (e) => { if (e.pointerType === "mouse") setPaused(false); };

  // Manual swipe — the arrows are intentionally hidden on mobile (see
  // below), so this is the primary way touch users navigate slides.
  const SWIPE_THRESHOLD_PX = 50;
  const touchStartX = useRef(null);
  const touchDeltaX = useRef(0);
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchDeltaX.current = 0;
    setPaused(true);
  };
  const onTouchMove = (e) => {
    if (touchStartX.current === null) return;
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
  };
  const onTouchEnd = () => {
    if (touchDeltaX.current > SWIPE_THRESHOLD_PX) goPrev();
    else if (touchDeltaX.current < -SWIPE_THRESHOLD_PX) goNext();
    touchStartX.current = null;
    touchDeltaX.current = 0;
    setPaused(false);
  };

  if (total === 0) return null;

  return (
    <section
      className="relative w-full aspect-[4/5] sm:aspect-[3/4] md:aspect-[21/9] lg:aspect-[2.5/1] overflow-hidden bg-[hsl(var(--kb-surface-alt))] group select-none touch-pan-y"
      data-testid="hero-asfashion"
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {slides.map((slide, idx) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
            idx === active ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"
          }`}
        >
          <picture>
            {slide.mobile_image_url && (
              <source media="(max-width: 767px)" srcSet={slide.mobile_image_url} />
            )}
            <img
              src={slide.image_url}
              alt={slide.title || `Slide ${idx + 1}`}
              className="w-full h-full object-cover object-top md:object-center group-hover:scale-[1.01] transition-transform duration-1000"
              loading={idx === 0 ? "eager" : "lazy"}
              fetchPriority={idx === 0 ? "high" : "auto"}
            />
          </picture>
          {/* Optional caption/CTA overlay — only if the admin supplied a title */}
          {(slide.title || slide.cta_label) && (
            <div className="absolute inset-0 flex items-end justify-start p-6 md:p-12">
              <div className="max-w-xl text-white drop-shadow-lg">
                {slide.subtitle && (
                  <div className="text-[10px] md:text-xs font-semibold uppercase tracking-[0.2em] mb-2 text-white/90">
                    {slide.subtitle}
                  </div>
                )}
                {slide.title && (
                  <h2 className="font-display text-2xl md:text-4xl lg:text-5xl font-light leading-tight">
                    {slide.title}
                  </h2>
                )}
                {slide.cta_label && (
                  <Link
                    to={slide.link_url}
                    className="mt-4 inline-block border border-white text-white px-6 py-2.5 text-xs md:text-sm font-medium uppercase tracking-[0.14em] hover:bg-white hover:text-[hsl(var(--kb-primary))] transition-all duration-300"
                    data-testid={`hero-cta-${idx}`}
                  >
                    {slide.cta_label}
                  </Link>
                )}
              </div>
            </div>
          )}
          <Link
            to={slide.link_url}
            aria-label={slide.title || `Open slide ${idx + 1}`}
            className="absolute inset-0 z-20"
          />
        </div>
      ))}

      {/* Left / right arrows — hidden on mobile, hover-revealed on md+.
         Matches asfashion.store's arrow behaviour. */}
      {total > 1 && (
        <>
          <button
            type="button"
            onClick={goPrev}
            aria-label="Previous slide"
            className="absolute left-4 top-1/2 -translate-y-1/2 z-30 p-2.5 rounded-full bg-black/10 hover:bg-black/30 text-white/80 hover:text-white transition-all duration-300 opacity-0 group-hover:opacity-100 hidden md:block"
            data-testid="hero-prev-btn"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            type="button"
            onClick={goNext}
            aria-label="Next slide"
            className="absolute right-4 top-1/2 -translate-y-1/2 z-30 p-2.5 rounded-full bg-black/10 hover:bg-black/30 text-white/80 hover:text-white transition-all duration-300 opacity-0 group-hover:opacity-100 hidden md:block"
            data-testid="hero-next-btn"
          >
            <ChevronRight size={24} />
          </button>
        </>
      )}

      {/* Slide indicator dots — only render when >1 slide. Active dot
         is wider + hot-pink to mirror asfashion.store. */}
      {total > 1 && (
        <div
          className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex items-center gap-2.5 z-30"
          data-testid="hero-dots"
        >
          {slides.map((_, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => setActive(idx)}
              aria-label={`Go to slide ${idx + 1}`}
              className={`h-2 rounded-full transition-all duration-300 ${
                idx === active
                  ? "bg-[hsl(var(--kb-accent))] w-6"
                  : "bg-white/60 hover:bg-white w-2"
              }`}
              data-testid={`hero-dot-${idx}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * AS Fashion category grid — small round "story chip" tiles, mirrors
 * asfashion.store's circular category rail (pink ring, hover scale).
 */
export function AsFashionCategoryGrid({ categories }) {
  const cats = categories.filter((c) => !c.parent_id).slice(0, 8);
  if (!cats.length) return null;
  return (
    <section className="mx-auto w-full px-4 sm:px-6 lg:px-8 py-8" data-testid="cat-asfashion">
      <div className="flex overflow-x-auto pt-4 pb-6 kb-scroll-hide justify-start md:justify-center items-center gap-4 md:gap-6">
        {cats.map((c) => (
          <Link
            key={c.id}
            to={`/shop/${c.slug}`}
            data-testid={`home-category-${c.slug}`}
            className="flex-shrink-0 flex flex-col items-center gap-2 group transition-all duration-300"
          >
            <div className="relative h-24 w-24 md:h-36 md:w-36 rounded-full border border-[hsl(var(--kb-accent))]/60 p-[3px] bg-white group-hover:border-[hsl(var(--kb-accent))] transition-all duration-300 shadow-sm group-hover:scale-110 group-hover:shadow-md">
              <div className="relative h-full w-full rounded-full overflow-hidden">
                {c.image_url ? (
                  <img
                    src={imgUrl(c.image_url)}
                    alt={c.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full bg-[hsl(var(--kb-surface-alt))]" />
                )}
              </div>
            </div>
            <span className="text-[11px] md:text-sm font-medium text-[hsl(var(--kb-text))] group-hover:text-[hsl(var(--kb-accent))] transition-colors text-center w-24 md:w-36 truncate">
              {c.name}
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}
